import React from 'react';
import { FaSearch } from 'react-icons/fa';
import config from '../../config.js';
import { resolveMediaUrl } from '../../_helpers/mediaUrl.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import styles from '../../styles/partial/SongFilter.module.css';

// Visibility filter values with their catalog labels:
// songList.filter.visibility.all, songList.filter.visibility.public, songList.filter.visibility.hidden
const VISIBILITY_OPTIONS = [
  { value: 'all', labelKey: 'songList.filter.visibility.all' },
  { value: 'public', labelKey: 'songList.filter.visibility.public' },
  { value: 'hidden', labelKey: 'songList.filter.visibility.hidden' },
];

const SongFilter = ({
  bandTags = [],
  selectedTagFilter,
  onFilterChange,
  visibilityFilter = 'all',
  onVisibilityFilterChange,
  searchTerm = '',
  onSearchChange = () => {},
}) => {
  const t = useT();

  const selectedId = selectedTagFilter
    ? String(selectedTagFilter.id ?? selectedTagFilter.ID ?? '')
    : null;

  const handleChipClick = (tag) => {
    const tagId = String(tag.id ?? tag.ID ?? '');
    onFilterChange(tagId === selectedId ? null : tag);
  };

  return (
    <div className={styles.filterSection}>
      <div className={styles.searchArea}>
        <FaSearch className={styles.searchIcon} aria-hidden="true" />
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('songList.filter.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={t('songList.filter.searchLabel')}
        />
      </div>

      <div className={styles.filterDivider} />

      <div className={styles.filterGroup}>
        {bandTags.length > 0 && (
          <>
            <span className={styles.filterGroupLabel}>{t('songList.filter.filterByTag')}</span>
            <div
              className={styles.inlineChipsRow}
              role="group"
              aria-label={t('songList.filter.filterByTagGroup')}
            >
              {bandTags.map((tag, index) => {
                const tagKey = String(tag.id ?? tag.ID ?? tag.word ?? tag.Word ?? index);
                const tagWord = tag.Word || tag.word;
                const tagBgColor = tag.BackgroundColor || tag.background_color;
                const tagTextColor = tag.TextColor || tag.text_color;
                const tagImageUrl = tag.ImageURL || tag.image_url;
                const usesLegacyDefaultColors = ['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'].includes(
                  String(tagBgColor).trim().toLowerCase()
                );
                const hasCustomColors = tagBgColor && !usesLegacyDefaultColors;
                const isSelected = String(tag.id ?? tag.ID ?? '') === selectedId;

                return (
                  <button
                    key={tagKey}
                    type="button"
                    className={`${styles.tagChipInline} ${isSelected ? styles.tagChipInlineActive : ''}`}
                    onClick={() => handleChipClick(tag)}
                    aria-pressed={isSelected}
                    style={hasCustomColors ? { backgroundColor: tagBgColor, color: tagTextColor } : undefined}
                  >
                    {tagImageUrl && (
                      <img
                        src={resolveMediaUrl(tagImageUrl)}
                        alt=""
                        className={styles.tagIconSmall}
                      />
                    )}
                    {tagWord}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {onVisibilityFilterChange && (
        <div className={styles.visibilityFilter} role="group" aria-label={t('songList.filter.filterByVisibility')}>
          {VISIBILITY_OPTIONS.map(({ value: v, labelKey }) => (
            <button
              key={v}
              type="button"
              className={`${styles.visibilityBtn} ${visibilityFilter === v ? styles.visibilityBtnActive : ''}`}
              onClick={() => onVisibilityFilterChange(v)}
              aria-pressed={visibilityFilter === v}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SongFilter;
